import React, { useState, useEffect, useCallback } from 'react';
import {
  Dna, UploadCloud, Loader2, AlertTriangle, CheckCircle2,
  User, Users, ShieldAlert, Stethoscope, Sparkles, Gauge,
  Download, History, ChevronDown, ChevronUp, FileWarning,
} from 'lucide-react';
import config from '../../../config';

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

function getAuthToken() {
  return localStorage.getItem('token');
}

async function apiRequest(path, options = {}) {
  const token = getAuthToken();
  const res = await fetch(`${config.baseURL}/api/couple-genetics${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    // /analyze returns which report(s) failed to parse (body.missing_roles),
    // so surface that alongside the message.
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.missingRoles = body.missing_roles || null;
    throw err;
  }
  return body.data;
}

const fetchReportStatus = () => apiRequest('/reports');
const fetchLatestAnalysis = () => apiRequest('/latest-analysis');
const fetchHistory = () => apiRequest('/history');
const uploadReport = (role, file) => {
  const form = new FormData();
  form.append('role', role);
  form.append('report', file);
  return apiRequest('/upload', { method: 'POST', body: form });
};
const giveConsent = () => apiRequest('/consent', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ consent: true }) });
const runAnalysis = () => apiRequest('/analyze', { method: 'POST' });

function downloadAnalysisPdf(id) {
  const token = getAuthToken();
  // Direct navigation (not fetch+blob) so the browser handles the
  // Content-Disposition download natively — simplest reliable approach
  // for an authenticated file download.
  const url = `${config.baseURL}/api/couple-genetics/analysis/${id}/pdf`;
  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((res) => res.blob())
    .then((blob) => {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'family-planning-score-summary.pdf';
      link.click();
      window.URL.revokeObjectURL(link.href);
    });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const RISK_TONE = {
  low: { color: '#0F6E56', bg: '#0F6E5612', label: 'Low risk' },
  moderate: { color: '#B4780A', bg: '#B4780A12', label: 'Moderate risk' },
  elevated: { color: '#B3261E', bg: '#B3261E12', label: 'Elevated risk' },
};

const CONFIDENCE_TONE = {
  high: { color: '#0F6E56', label: 'High confidence' },
  moderate: { color: '#B4780A', label: 'Moderate confidence' },
  low: { color: '#B3261E', label: 'Low confidence' },
};

const SCORE_ROWS = [
  { key: 'hla', label: 'HLA Compatibility', weight: 10 },
  { key: 'psychogenetic', label: 'Psychogenetic', weight: 40 },
  { key: 'reproductive', label: 'Reproductive', weight: 5 },
  { key: 'biometricSynchrony', label: 'Biometric Synchrony', weight: 45 },
];

function scoreTone(score) {
  if (score >= 70) return '#0F6E56';
  if (score >= 40) return '#B4780A';
  return '#B3261E';
}

// ---------------------------------------------------------------------------
// Extraction-quality banner. Shown whenever the analyze response says one
// or both PDFs looked corrupted on the way in (see coupleGeneticAI.js's
// corruptionScore check, tuned against real black-box-redacted lab
// reports). This exists so a report degraded by PDF text corruption is
// shown as a caveated best-effort reading rather than presented with the
// same confidence as a clean report — the findings below it are real,
// not placeholder data, but some condition names in them may be a
// best-effort reading of garbled text.
// ---------------------------------------------------------------------------

function ExtractionWarningBanner({ warning }) {
  if (!warning) return null;

  const who =
    warning.self && warning.partner
      ? 'Both uploaded reports'
      : warning.self
        ? 'Your report'
        : "Your partner's report";

  return (
    <div className="chart-card" style={{ borderColor: '#B4780A', backgroundColor: '#B4780A0D' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <FileWarning size={18} color="#B4780A" style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#7A5206' }}>
            {who} may have text-extraction issues
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#5F5E5A' }}>
            {warning.message}
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload slot
// ---------------------------------------------------------------------------

function ReportSlot({ role, label, icon: Icon, report, parseError, onUpload, uploading }) {
  const inputId = `couple-genetic-upload-${role}`;
  // A report the server couldn't read is shown as a problem to fix, not
  // as "uploaded successfully" — parsed === false on an existing report
  // means it's stored but useless for analysis until replaced.
  const needsReupload = report && !report.parsed;

  return (
    <div className="chart-card" style={needsReupload ? { borderColor: '#B3261E' } : undefined}>
      <div className="chart-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 32, height: 32, borderRadius: 8, background: '#0F4C7514', color: '#0F4C75',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon size={16} />
          </span>
          <h3 className="chart-title" style={{ margin: 0 }}>{label}</h3>
        </div>
      </div>

      {report ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {needsReupload ? (
            <AlertTriangle size={18} color="#B3261E" style={{ flexShrink: 0 }} />
          ) : (
            <CheckCircle2 size={18} color="#0F6E56" style={{ flexShrink: 0 }} />
          )}
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {report.original_name}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 11.5, color: needsReupload ? '#B3261E' : '#888780' }}>
              Uploaded {formatDate(report.uploaded_at)}{needsReupload ? ' · could not read text from this PDF' : ''}
            </p>
          </div>
        </div>
      ) : (
        <p className="muted-text" style={{ fontSize: 13 }}>No report uploaded yet.</p>
      )}

      {/* The exact reason, straight from the upload response, so the
          uploader knows what to do (re-scan vs. try a different file)
          instead of just "could not read text". */}
      {parseError && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#B3261E' }}>{parseError}</p>
      )}

      <label
        htmlFor={inputId}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 14px',
          borderRadius: 8, border: `1px dashed ${needsReupload ? '#B3261E' : '#0F4C75'}`, color: needsReupload ? '#B3261E' : '#0F4C75', fontSize: 13, fontWeight: 600,
          cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.7 : 1,
        }}
      >
        {uploading ? <Loader2 size={14} className="spin" /> : <UploadCloud size={14} />}
        {uploading ? 'Uploading…' : needsReupload ? 'Re-upload PDF' : report ? 'Replace file' : 'Upload PDF'}
        <input
          id={inputId}
          type="file"
          accept="application/pdf"
          disabled={uploading}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) onUpload(role, file);
          }}
        />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Consent gate. Shown once both reports are uploaded, before the
// "Run compatibility analysis" button becomes available.
// ---------------------------------------------------------------------------

function ConsentGate({ consented, onConsent, consenting }) {
  const [checked, setChecked] = useState(false);

  if (consented) return null;

  return (
    <div className="chart-card" style={{ borderColor: '#0F4C75', backgroundColor: '#0F4C7508' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <ShieldAlert size={18} color="#0F4C75" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>Consent required before analysis</p>
          <p style={{ margin: '4px 0 10px', fontSize: 12.5, color: '#5F5E5A' }}>
            This will send both partners' genetic report contents to an AI service for analysis. Both reports were uploaded by you on behalf of both partners — by proceeding, you confirm you have both partners' permission to share this information for this purpose.
          </p>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: '#3A3A38', cursor: 'pointer', marginBottom: 10 }}>
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} style={{ marginTop: 2 }} />
            I confirm I have permission from both partners to share this genetic information for AI-assisted analysis.
          </label>
          <button onClick={onConsent} disabled={!checked || consenting} className="btn-primary">
            {consenting ? 'Recording consent…' : 'I consent — continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Family Planning Score card
// ---------------------------------------------------------------------------

function FamilyPlanningScoreCard({ scores, keyInsights, dataCompleteness }) {
  if (!scores) return null;

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 32, height: 32, borderRadius: 8, background: '#8B5CF614', color: '#8B5CF6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Gauge size={16} />
          </span>
          <h3 className="chart-title" style={{ margin: 0 }}>Family Planning Score</h3>
        </div>
        <span
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999,
            background: `${scoreTone(scores.familyPlanningScore)}14`, color: scoreTone(scores.familyPlanningScore),
            fontSize: 16, fontWeight: 800,
          }}
        >
          {scores.familyPlanningScore?.toFixed?.(1) ?? scores.familyPlanningScore}
        </span>
      </div>

      {dataCompleteness != null && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: '#888780', marginBottom: 4 }}>
            <span>Estimated screening completeness</span>
            <span>{dataCompleteness}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 999, background: '#EEF0F2', overflow: 'hidden' }}>
            <div style={{ width: `${dataCompleteness}%`, height: '100%', background: scoreTone(dataCompleteness), borderRadius: 999 }} />
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
          <thead>
            <tr>
              {['Category', 'Score', 'Weight', 'Weighted'].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#777', borderBottom: '1px solid #EEF0F2' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SCORE_ROWS.map((row) => {
              const score = scores[row.key] ?? 0;
              const weighted = (score * row.weight) / 100;
              return (
                <tr key={row.key}>
                  <td style={{ padding: '9px 10px', fontSize: 13, fontWeight: 600, borderBottom: '1px solid #F2F3F4' }}>{row.label}</td>
                  <td style={{ padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #F2F3F4', color: scoreTone(score) }}>{score}%</td>
                  <td style={{ padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #F2F3F4', color: '#888780' }}>{row.weight}%</td>
                  <td style={{ padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #F2F3F4', fontWeight: 600 }}>{weighted.toFixed(1)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(scores.hla === 50 || scores.psychogenetic === 50 || scores.biometricSynchrony === 50) && (
        <p style={{ fontSize: 11.5, color: '#888780', marginBottom: 14 }}>
          Scores shown as neutral (50%) indicate the underlying data wasn't available — not an actual finding.
        </p>
      )}

      {keyInsights && keyInsights.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Sparkles size={14} color="#8B5CF6" />
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>Key insights</h4>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#3A3A38', display: 'grid', gap: 5 }}>
            {keyInsights.map((insight, i) => <li key={i}>{insight}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Analysis result — comprehensive findings + confidence badges + PDF
// download. Renders every entry in analysis.findings — a full report can
// legitimately carry hundreds of them (real Natera/Horizon-style panels
// screen 400-600+ conditions), so this is not truncated or sampled.
// ---------------------------------------------------------------------------

function AnalysisResult({ analysis, onBookAppointment }) {
  const tone = RISK_TONE[analysis.risk_level] || RISK_TONE.low;

  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3 className="chart-title">Birth risk summary</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
              background: tone.bg, color: tone.color, fontSize: 12.5, fontWeight: 700,
            }}
          >
            <ShieldAlert size={14} /> {tone.label}
          </span>
          {typeof analysis.id === 'number' && (
            <button
              onClick={() => downloadAnalysisPdf(analysis.id)}
              title="Download PDF summary"
              style={{ border: '1px solid #E5E7EB', background: 'white', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <Download size={15} color="#0F4C75" />
            </button>
          )}
        </div>
      </div>

      <p style={{ fontSize: 13.5, color: '#3A3A38', marginBottom: 16 }}>{analysis.summary}</p>

      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        {analysis.findings.map((f, i) => {
          const conf = CONFIDENCE_TONE[f.confidence] || CONFIDENCE_TONE.moderate;
          return (
            <div key={i} style={{ border: '1px solid #EEF0F2', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{f.condition}</p>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: conf.color, whiteSpace: 'nowrap' }}>{conf.label}</span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#5F5E5A' }}>
                You: <strong>{f.selfStatus}</strong> · Partner: <strong>{f.partnerStatus}</strong>
              </p>
              {f.combinedRisk && f.combinedRisk !== 'none' && (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#B4780A', fontWeight: 600 }}>{f.combinedRisk}</p>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: 12, background: '#F7F8FA', borderRadius: 10, marginBottom: 14 }}>
        <AlertTriangle size={16} color="#B4780A" style={{ flexShrink: 0, marginTop: 1 }} />
        <p style={{ margin: 0, fontSize: 12.5, color: '#5F5E5A' }}>{analysis.recommendation}</p>
      </div>

      <button onClick={onBookAppointment} className="btn-primary">
        <Stethoscope size={15} /> Book a genetic counseling appointment
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible history of previous analyses
// ---------------------------------------------------------------------------

function AnalysisHistory({ history }) {
  const [open, setOpen] = useState(false);

  if (!history || history.length <= 1) return null; // nothing to show beyond the current one

  const previous = history.slice(1); // history[0] is the latest, shown above already

  return (
    <div className="chart-card">
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <History size={16} color="#888780" />
          <span style={{ fontSize: 13, fontWeight: 700 }}>Previous analyses ({previous.length})</span>
        </div>
        {open ? <ChevronUp size={16} color="#888780" /> : <ChevronDown size={16} color="#888780" />}
      </button>

      {open && (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {previous.map((a) => {
            const tone = RISK_TONE[a.risk_level] || RISK_TONE.low;
            return (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #EEF0F2', borderRadius: 8, padding: 10 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>{formatDate(a.created_at)}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11.5, color: tone.color }}>{tone.label} · Score {a.scores.familyPlanningScore}</p>
                </div>
                {typeof a.id === 'number' && (
                  <button
                    onClick={() => downloadAnalysisPdf(a.id)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#0F4C75', display: 'flex', alignItems: 'center' }}
                    title="Download PDF"
                  >
                    <Download size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

const CoupleGeneticTab = ({ onNavigateToAppointments }) => {
  const [reports, setReports] = useState({ self: null, partner: null });
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set only when this tab genuinely could not reach the API, so that
  // failure is shown honestly instead of being papered over with
  // placeholder demo data — a "Cystic Fibrosis carrier match" that isn't
  // real is worse than an honest "couldn't load your reports" message.
  const [loadError, setLoadError] = useState(null);
  const [uploadingRole, setUploadingRole] = useState(null);
  const [consenting, setConsenting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  // Per-role reason a report failed to parse, e.g.
  // { self: 'This PDF has no selectable text...' }. Filled in right after
  // upload (from upload's response) and again if /analyze reports which
  // role(s) are still missing.
  const [parseErrors, setParseErrors] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    Promise.all([fetchReportStatus(), fetchLatestAnalysis(), fetchHistory()])
      .then(([reportData, analysisData, historyData]) => {
        setReports(reportData || { self: null, partner: null });
        setAnalysis(analysisData || null);
        setHistory(historyData && historyData.length ? historyData : (analysisData ? [analysisData] : []));
      })
      .catch((err) => {
        setLoadError(err.message || 'Could not load your genetic reports right now.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleUpload(role, file) {
    setUploadingRole(role);
    setError(null);
    setParseErrors((prev) => ({ ...prev, [role]: null }));
    try {
      const uploaded = await uploadReport(role, file);
      // Show the reason immediately if this specific PDF couldn't be read,
      // rather than waiting for the uploader to hit "Run analysis" later.
      setParseErrors((prev) => ({ ...prev, [role]: uploaded?.parse_error || null }));
      await load();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploadingRole(null);
    }
  }

  async function handleConsent() {
    setConsenting(true);
    setError(null);
    try {
      await giveConsent();
      await load();
    } catch (err) {
      setError(err.message || 'Could not record consent');
    } finally {
      setConsenting(false);
    }
  }

  async function handleAnalyze() {
    setAnalyzing(true);
    setError(null);
    try {
      const result = await runAnalysis();
      setAnalysis(result);
      setHistory((prev) => [result, ...prev]);
    } catch (err) {
      setError(err.message || 'Analysis failed');
      // 422 from /analyze names which report(s) still have no text —
      // reflect that on the matching upload slot(s) too.
      if (err.missingRoles && err.missingRoles.length) {
        setParseErrors((prev) => {
          const next = { ...prev };
          err.missingRoles.forEach((role) => {
            next[role] = next[role] || 'Could not read text from this PDF — please re-upload it.';
          });
          return next;
        });
      }
    } finally {
      setAnalyzing(false);
    }
  }

  const bothUploaded = reports.self && reports.partner;
  const bothParsed = reports.self?.parsed && reports.partner?.parsed;
  const bothConsented = reports.self?.consented && reports.partner?.consented;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: '#888780' }}>
        <Loader2 size={20} className="spin" style={{ marginBottom: 8 }} />
        <p style={{ margin: 0, fontSize: 13 }}>Loading…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="section-stack">
        <div>
          <h2 style={{ margin: '0 0 5px', fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Dna size={20} color="#0F4C75" /> Couple DNA & birth risk
          </h2>
        </div>
        <div className="chart-card" style={{ borderColor: '#B3261E', backgroundColor: '#B3261E0D' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <AlertTriangle size={18} color="#B3261E" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#B3261E' }}>Couldn't load your reports</p>
              <p style={{ margin: '4px 0 10px', fontSize: 12.5, color: '#5F5E5A' }}>{loadError}</p>
              <button onClick={load} className="btn-primary">Try again</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-stack">
      <style>{`.spin { animation: cg-spin 1s linear infinite; } @keyframes cg-spin { to { transform: rotate(360deg); } }`}</style>

      <div>
        <h2 style={{ margin: '0 0 5px', fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Dna size={20} color="#0F4C75" /> Couple DNA & birth risk
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: '#888780' }}>
          Upload both partners' genetic/carrier-screening reports for an AI-assisted cross-analysis of inherited-condition risk.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        <ReportSlot role="self" label="Your report" icon={User} report={reports.self} parseError={parseErrors.self} onUpload={handleUpload} uploading={uploadingRole === 'self'} />
        <ReportSlot role="partner" label="Partner's report" icon={Users} report={reports.partner} parseError={parseErrors.partner} onUpload={handleUpload} uploading={uploadingRole === 'partner'} />
      </div>

      {error && (
        <div className="chart-card" style={{ borderColor: '#B3261E', backgroundColor: '#B3261E0D' }}>
          <p style={{ margin: 0, fontSize: 13, color: '#B3261E' }}>{error}</p>
        </div>
      )}

      {bothUploaded && !bothParsed && (
        <p className="muted-text" style={{ fontSize: 13 }}>
          Re-upload the report(s) marked above before continuing — analysis needs selectable text from both.
        </p>
      )}

      {bothUploaded && bothParsed && !bothConsented && (
        <ConsentGate consented={bothConsented} onConsent={handleConsent} consenting={consenting} />
      )}

      {bothUploaded && bothParsed && bothConsented && (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <button onClick={handleAnalyze} disabled={analyzing} className="btn-primary">
            {analyzing ? <Loader2 size={15} className="spin" /> : <Dna size={15} />}
            {analyzing ? 'Analyzing both reports…' : analysis ? 'Re-run analysis' : 'Run compatibility analysis'}
          </button>
        </div>
      )}

      {!bothUploaded && (
        <p className="muted-text" style={{ fontSize: 13 }}>Upload both reports above to run the analysis.</p>
      )}

      {analysis?.extractionWarning && <ExtractionWarningBanner warning={analysis.extractionWarning} />}
      {analysis && <FamilyPlanningScoreCard scores={analysis.scores} keyInsights={analysis.key_insights} dataCompleteness={analysis.data_completeness} />}
      {analysis && <AnalysisResult analysis={analysis} onBookAppointment={onNavigateToAppointments} />}
      <AnalysisHistory history={history} />
    </div>
  );
};

export default CoupleGeneticTab;